/**
 * The Sold Works ledger's own copy.
 *
 * A separate block from `GALLERY_COPY` rather than more keys in it, because the
 * two screens are about different rows and one of them is about MONEY. Every
 * sentence here has to survive being read by a director beside a price; keeping
 * them together with the inventory's would make it easy to reuse a word from a
 * screen where nothing has been sold.
 */
export const SOLD_COPY = {
  loading: "Reading your ledger…",
  errorTitle: "Can’t load your sold works",

  /**
   * Unsaying a sale, in two presses.
   *
   * "End this sale" and not "Delete": `ondo.withdraw_transfer()` sets the row's
   * status to `withdrawn` and voids any settlements it raised — nothing is
   * removed, and the ledger keeps the line. A label promising erasure would
   * describe a different registry from this one.
   *
   * Offered only while the sale is still open. Once a buyer has ACCEPTED,
   * undoing it is a new transfer back to the seller and not a correction of
   * this row, which is why the function refuses `accepted` outright.
   */
  withdraw: "End this sale",
  withdrawConfirm: "Yes, end it",
  withdrawCancel: "Keep it",
  withdrawNote:
    "The sale stays on the ledger, marked ended, and anything it owed under a covenant is voided. The work returns to nobody — this records that the sale did not complete.",

  /**
   * Says how a row gets here, because a gallery cannot put one here itself.
   *
   * A sale is `ondo.effect_sale()` on a consignment, so an empty ledger with no
   * explanation would read as a broken screen to a firm that has plenty of
   * stock. It points at the screen the act actually happens on, which is the
   * same courtesy the inventory's empty state pays the other way round.
   */
  empty: "You have not placed a work through ONDO yet.",
  emptyDetail:
    "A sale is recorded against the consignment it was made under, from Inventory. When one is, the work leaves your inventory and appears here with its buyer, its price and any covenant it carries.",

  /** 'provenance ledger · 4 works placed'. The design's own header line. */
  placed: (n: number) => `provenance ledger · ${n} ${n === 1 ? "work" : "works"} placed`,

  /**
   * The lead paragraph over the list.
   *
   * The design's version says "full code history". This says the code and stops,
   * because there is no history to show: an ONDO code is issued once and never
   * reissued, so a work does not get a second one when it sells. What changes is
   * the STATE of the one code, which is the badge on the row — see `codeNote`.
   */
  lead: "Every work you have placed, with its buyer, what it made, and where its title now is.",
  leadAction: "Open any work for its full record.",

  /**
   * Why there is no `OND-…-R` beside the code, which the design shows.
   *
   * The decision not to override the code SHAPE was taken deliberately and this
   * is the same decision one layer down: a suffix printed after a sale would
   * read as a second, reissued code, and there is not one. `ondo.works.ondo_code`
   * is minted at registration and is the only address the work ever has. What a
   * sale changes is what the registry HOLDS about it, and that is the red badge
   * and the date beside it.
   */
  codeNote:
    "The code does not change when a work sells. What changes is what the registry holds about it.",

  /** Where title is. The distinction the whole ledger turns on. */
  title: {
    moved: "Title transferred",
    /**
     * Says what is outstanding without implying anything has gone wrong.
     *
     * A buyer on ONDO has to accept before title moves, and until they do the
     * sale is complete and the registry is not. That is an ordinary state, not
     * a failure, and the wording has to survive sitting on a row for weeks.
     */
    awaiting: "Awaiting buyer",
    /**
     * The ordinary case, said plainly.
     *
     * Most art buyers are not on ONDO. "Buyer not on ONDO" is the honest reading
     * of `status = 'unregistered'`: nobody was asked, so nothing is awaited, and
     * the work is still placed where it was because there is no principal here
     * to put it with.
     */
    off_registry: "Buyer not on ONDO",
  },

  /** The badge the design puts on the newest row. */
  justSold: "Just sold",
  justSoldDays: 7,

  /** What a post-sale covenant is doing. Derived from its end date, never stored. */
  covenant: {
    active: "Active",
    expired: "Expired",
    /**
     * A lock that was lifted, which is NOT the same as a term that ran out.
     *
     * `ondo.unlock_covenant()` records a release and never edits a term, so the
     * row still carries its share and its split; what it no longer does is stop
     * the work being sold on. A screen that showed this as "Expired" would be
     * reporting somebody's decision as the passage of time.
     */
    unlocked: "Unlocked",
  },
  covenantEnded: "ended",
  remaining: "remaining",
  underAMonth: "under a month remaining",
  covenantTerms: "Post-sale covenant",

  soldOn: "sold",

  /** The filter over the list, and it is by ARTIST — the inventory's argument. */
  artistFilter: "Artist",
  allArtists: "All",
  noMatches: "You have placed no work by that artist.",
} as const;
